/**
 * The support route tree — Task 13.1, requirements 9.3, 10.1, and 15.1–15.5.
 *
 * Two sibling routes, `/support` and `/support/:publicId`, mounted in the authenticated shell beside the
 * Content Console rather than inside it. Support is not a content type: it has its own status vocabulary,
 * assignee, notes, and events, and `/content/:type` would be the wrong shape for all four.
 *
 * Every authenticated staff role reaches these, `agente` included. That is the difference from the Content
 * Console, which an agente cannot open at all — triage is the work an agente is for.
 *
 * The screens here render the states and the identity of what was loaded. Task 13.2 builds the accessible
 * queue with its filters and pagination, and 13.3 the detail workflow with the role matrix; both consume the
 * hooks this task wires, so neither has to reach for a second data layer.
 */
import type { SupportRequestDetail, SupportRequestList } from '@apuestapr/contracts';
import { Skeleton } from '@apuestapr/ui';
import { useState, type ReactElement } from 'react';
import { Link, Route, Routes, useParams } from 'react-router-dom';

import type { ApiFailure } from '../api/client.ts';
import type { RequestState } from '../api/request-state.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { useManagedSupportRequest, useSupportQueue } from './hooks.ts';
import { supportRoutePath } from './paths.ts';
import { SupportRequestView } from './SupportRequestView.tsx';
import {
  SupportQueueEmpty,
  SupportQueueFiltersForm,
  SupportQueuePagination,
  SupportQueueTable,
  emptySupportQueueFilters,
  supportQueryFor,
  type SupportQueueFilters,
} from './SupportQueue.tsx';

export { supportRequestPath, supportRoutePath } from './paths.ts';

/**
 * One sentence per failure, and a 403 says something different from a fault.
 *
 * Kept here rather than in the client so the words belong to the screen: the transport reports what happened
 * and the page decides what a reader is told about it.
 */
export const supportFailureMessage = (
  failure: ApiFailure,
  access: 'forbidden' | 'failure',
): string => {
  if (access === 'forbidden') return 'Tu rol no tiene permiso para esta operación.';
  if (failure.kind === 'unreachable') return 'No pudimos conectar con la API. Inténtalo de nuevo.';
  if (failure.kind === 'malformed') {
    return 'La respuesta no cumple el contrato esperado. Ya estamos revisándolo.';
  }
  if (failure.status === 401) return 'Tu sesión expiró. Vuelve a iniciar sesión.';
  if (failure.status === 404) return 'Esa solicitud de soporte no existe.';
  return 'La API respondió con un error. Inténtalo de nuevo.';
};

/**
 * Shaped like the row-and-detail layout either screen settles into, so the space is already held when
 * the real content answers. A single `role="status"` announcement covers it, the same reasoning
 * `Skeleton` documents for why it carries no ARIA role of its own.
 */
const SupportLoadingSkeleton = (): ReactElement => (
  <div aria-hidden="true" className="grid gap-3 rounded-xl border border-border bg-card p-4">
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex items-center gap-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-32" />
      </div>
    ))}
  </div>
);

/** The shared loading/error framing, so both screens report the same way. */
const StateFrame = <Value,>({
  state,
  children,
}: {
  readonly state: RequestState<Value>;
  readonly children: (data: Value) => ReactElement;
}): ReactElement => {
  if (state.status === 'loading')
    return (
      <>
        <p className="sr-only" role="status">
          Cargando…
        </p>
        <SupportLoadingSkeleton />
      </>
    );
  if (state.status === 'error') {
    return <p role="alert">{supportFailureMessage(state.failure, state.access)}</p>;
  }
  return children(state.data);
};

/**
 * The queue screen — Task 13.2.
 *
 * Filters and the current page are component state, never the URL: the search box takes free text a staff member
 * types to find a person, so it can hold a name or an email fragment, and a URL is the least private place in a
 * browser. See `SupportQueue.tsx` for the full reasoning. Nothing is written to browser storage either.
 */
const QueuePage = (): ReactElement => {
  const session = useSessionContext();
  const [filters, setFilters] = useState<SupportQueueFilters>(emptySupportQueueFilters);
  const [page, setPage] = useState(1);
  const selfId = session.state.status === 'authenticated' ? session.state.session.user.id : 0;
  const state = useSupportQueue(session.client, supportQueryFor(filters, page, selfId));

  const filtered = JSON.stringify(filters) !== JSON.stringify(emptySupportQueueFilters);
  const clear = (): void => {
    setFilters(emptySupportQueueFilters);
    setPage(1);
  };

  return (
    <section aria-labelledby="support-queue">
      <h2 id="support-queue">Solicitudes de soporte</h2>
      <SupportQueueFiltersForm
        filters={filters}
        disabled={state.status === 'loading'}
        onApply={(next) => {
          // A new filter set is a new result set, so it starts at the first page rather than keeping an
          // offset that may no longer exist.
          setFilters(next);
          setPage(1);
        }}
      />
      <StateFrame state={state}>
        {(list: SupportRequestList) =>
          list.items.length === 0 ? (
            <SupportQueueEmpty filtered={filtered} onClear={clear} />
          ) : (
            <>
              <SupportQueueTable list={list} />
              <SupportQueuePagination
                list={list}
                disabled={state.status === 'loading'}
                onPage={setPage}
              />
            </>
          )
        }
      </StateFrame>
    </section>
  );
};

const RequestPage = (): ReactElement => {
  const session = useSessionContext();
  const { publicId = '' } = useParams<{ publicId: string }>();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const csrfToken = authenticated?.csrfToken ?? '';
  const role = authenticated?.user.rol ?? 'agente';
  const selfId = authenticated?.user.id ?? 0;
  const managed = useManagedSupportRequest(session.client, publicId, csrfToken);

  return (
    <section aria-labelledby="support-request">
      <h2 id="support-request">Solicitud de soporte</h2>
      <Link to={supportRoutePath}>Volver a la cola</Link>
      <StateFrame state={managed.state}>
        {(detail: SupportRequestDetail) => (
          <>
            <h3 className="mt-4">{detail.publicId}</h3>
            <SupportRequestView detail={detail} role={role} selfId={selfId} managed={managed} />
          </>
        )}
      </StateFrame>
    </section>
  );
};

export const SupportRouteTree = (): ReactElement => (
  <Routes>
    <Route path="/" element={<QueuePage />} />
    <Route path="/:publicId" element={<RequestPage />} />
  </Routes>
);
